import React, { Dispatch, FunctionComponent, MouseEventHandler, useEffect, useState } from 'react';
import { TransformComponent } from 'react-zoom-pan-pinch';

import useEvent from 'hooks/useEvent';
import { getPDPTrackingPropsFormatted } from 'helpers/ProductUtils';
import { FeaturedImage } from 'types/product';
import { AriaLiveTee } from 'components/common/AriaLive';
import { track } from 'apis/amethyst';
import { evFeaturedZoom, evZoomIn, evZoomOut } from 'events/product';
import useMartyContext from 'hooks/useMartyContext';

import css from 'styles/components/common/zoom/imageZoomControls.scss';

interface ZoomControlsProps {
  scale: number;
  zoomIn: MouseEventHandler<HTMLButtonElement>;
  zoomOut: MouseEventHandler<HTMLButtonElement>;
  positionX: number;
  positionY: number;
  setPositionX: Dispatch<React.SetStateAction<number>>;
  setPositionY: Dispatch<React.SetStateAction<number>>;
  minScale: number;
  maxScale: number;
}

/**
 * PDP Assets gallery zoom controls
 * @returns {FunctionComponent}
 */
const ZoomControls: FunctionComponent<ZoomControlsProps> = ({
  scale,
  minScale,
  maxScale,
  zoomIn,
  zoomOut,
  positionX,
  positionY,
  setPositionX,
  setPositionY
}) => {
  const { testId } = useMartyContext();

  // Handler for panning trough the image using arrow keys
  useEvent(document, 'keyup', ((keyboard: KeyboardEvent) => {
    const moveFactor = 25;
    switch (keyboard.code) {
      case 'ArrowUp':
        setPositionY(positionY + moveFactor);
        break;
      case 'ArrowDown':
        setPositionY(positionY - moveFactor);
        break;
      case 'ArrowLeft':
        setPositionX(positionX + moveFactor);
        break;
      case 'ArrowRight':
        setPositionX(positionX - moveFactor);
        break;
      default:
        break;
    }
  }) as EventListener);

  const handleZoomOutClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    track(() => [evZoomOut, {}]);
    zoomOut(e);
  };

  const handleZoomInClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    track(() => [evZoomIn, {}]);
    zoomIn(e);
  };

  return (
    <div className={css.zoomControls}>
      <button
        className={css.zoomControlButton}
        type="button"
        onClick={handleZoomOutClick}
        aria-label="Zoom Out"
        disabled={scale === minScale}
        data-test-id={testId('zoomOut')}
        {...getPDPTrackingPropsFormatted('Zoom-Out', 'Zoom-Control-Click')}
      />
      <div data-test-id={testId('zoomValue')}>{`${Math.round(scale * 100)}%`}</div>
      <button
        className={css.zoomControlButton}
        type="button"
        onClick={handleZoomInClick}
        aria-label="Zoom In"
        disabled={scale === maxScale}
        data-test-id={testId('zoomIn')}
        {...getPDPTrackingPropsFormatted('Zoom-In', 'Zoom-Control-Click')}
      />
    </div>
  );
};

interface ZoomInstructionsProps {
  showInstructions: boolean;
  setShowInstructions: Dispatch<React.SetStateAction<boolean>>;
}

/**
 * PDP Assets gallery zoom instructions banner
 * @returns {FunctionComponent}
 */
const ZoomInstructions: FunctionComponent<ZoomInstructionsProps> = ({ showInstructions, setShowInstructions }) => {
  const { testId } = useMartyContext();
  return showInstructions ? (
    <AriaLiveTee>
      <div className={css.zoomBanner} data-test-id={testId('productGalleryZoomBanner')}>
        <div>Drag image to see more details</div>
        <button
          className={css.bannerButton}
          type="button"
          onClick={() => setShowInstructions(false)}
          aria-label="Close zoom instructions"
          data-test-id={testId('productGalleryZoomBannerClose')}
          {...getPDPTrackingPropsFormatted('Zoom-Instructions-Close', 'Button-Click')}
        />
      </div>
    </AriaLiveTee>
  ) : null;
};

interface ImageZoomControlsProps {
  scale: number;
  zoomIn: MouseEventHandler<HTMLButtonElement>;
  zoomOut: MouseEventHandler<HTMLButtonElement>;
  positionX: number;
  positionY: number;
  setPositionX: Dispatch<React.SetStateAction<number>>;
  setPositionY: Dispatch<React.SetStateAction<number>>;
  image: FeaturedImage;
  showInstructions: boolean;
  setShowInstructions: Dispatch<React.SetStateAction<boolean>>;
  minScale: number;
  maxScale: number;
  resetTransform: () => void;
}

/**
 * PDP Assets gallery zoomed image
 * @returns {FunctionComponent}
 */
const ImageZoomControls: FunctionComponent<ImageZoomControlsProps> = ({
  scale,
  minScale,
  maxScale,
  zoomIn,
  zoomOut,
  positionX,
  positionY,
  setPositionX,
  setPositionY,
  image,
  showInstructions,
  setShowInstructions,
  resetTransform
}) => {
  const { testId } = useMartyContext();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const handleImageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const { detail } = event;
    const DOUBLE_CLICK = 2;

    if (detail === DOUBLE_CLICK) {
      trackZoomEvent();
    }
  };

  const trackZoomEvent = () => {
    const {
      thumbnail: { src },
      alt
    } = image;
    track(() => [evFeaturedZoom, { src, alt }]);
  };

  const handleImageLoad = () => {
    if (!isImageLoaded) {
      trackZoomEvent();
      setTimeout(zoomIn, 250, {});
      setIsImageLoaded(true);
    }
  };

  // if the image changes reset the zoom
  useEffect(() => {
    if (isImageLoaded) {
      resetTransform();
      setTimeout(zoomIn, 250, {});
    }
  }, [isImageLoaded, resetTransform, zoomIn, image]);

  return (
    <>
      <ZoomInstructions showInstructions={showInstructions} setShowInstructions={setShowInstructions} />
      <ZoomControls
        scale={scale}
        minScale={minScale}
        maxScale={maxScale}
        zoomIn={zoomIn}
        zoomOut={zoomOut}
        positionX={positionX}
        positionY={positionY}
        setPositionX={setPositionX}
        setPositionY={setPositionY}
      />
      <TransformComponent>
        <button type="button" className={css.imageButton} onClick={handleImageButtonClick}>
          <picture>
            {image.zoom.webp && <source srcSet={`${image.zoom.webp.src} 1x, ${image.zoom.webp.retinaSrc}`} type="image/webp" />}
            <source srcSet={`${image.zoom.src} 1x, ${image.zoom.retinaSrc}`} type="image/jpeg" />
            <img
              data-test-id={testId('zoomedImg')}
              alt={`${image.alt} Zoom`}
              src={image.zoom.src}
              srcSet={image.zoom.retinaSrc}
              onLoad={handleImageLoad}
            />
          </picture>
        </button>
      </TransformComponent>
    </>
  );
};

export default ImageZoomControls;
